// @ts-check
// Seções "Personalização" e "Valores" do editor de campos (doc 11, 3.6.5; doc 04, 2.3 e 2.5): pré-visualização,
// settings do tipo, valor padrão, campo único e índice simples. O formulário mantém o rascunho; aqui só se desenha.
import { html } from 'htm/preact';
import {
  CLICK_ACTIONS,
  CURRENCY_FORMATS,
  DATE_DISPLAY_FORMATS,
  MAX_CURRENCY_DECIMALS,
  MAX_DISPLAYED_ROWS,
  MAX_NUMBER_DECIMALS,
  NUMBER_DATA_TYPES,
  NUMBER_VARIANTS,
} from '../../../../../shared/field-types/field-settings.js';
import { PHONE_COUNTRIES } from '../../../lib/phone.js';
import { FieldDisplay } from '../../object-record/field-display.js';
import { Icon } from '../../../ui/icon.js';
import { Toggle } from '../../../ui/feedback.js';
import { LOCALE, t, translateMetadataLabel } from '../../../i18n/index.js';
import { SettingsSection } from '../settings-pages.js';
import { DEFAULT_VALUE_EDITOR_TYPES, canFieldBeIndexed, canTypeBeUnique, getUniqueLockReason } from './field-customization-draft.js';

/**
 * @typedef {import('../../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {import('./field-customization-draft.js').CustomizationDraft} CustomizationDraft
 * @typedef {{ value: string, label: string }} ChoiceOption
 */

// moedas mais comuns para escola brasileira e vizinhos; o nome vem do Intl, em pt-BR
const CURRENCY_CODES = Object.freeze(['BRL', 'USD', 'EUR', 'GBP', 'ARS', 'CLP', 'COP', 'MXN', 'PYG', 'UYU', 'PEN', 'BOB', 'CAD', 'JPY', 'CNY', 'AOA', 'MZN']);
const RATING_STEPS = Object.freeze([1, 2, 3, 4, 5]);

/** @param {string} code */
function currencyName(code) {
  try {
    return new Intl.DisplayNames(LOCALE, { type: 'currency' }).of(code) ?? code;
  } catch {
    return code;
  }
}

// valores de exemplo da pré-visualização, como o SettingsDataModelFieldPreviewWidget do Twenty
const PREVIEW_DATE_OFFSET_DAYS = 3;

/** @param {string} type @param {CustomizationDraft} draft @param {Array<{ value: string }>} options */
function previewValue(type, draft, options) {
  const past = new Date(Date.now() - PREVIEW_DATE_OFFSET_DAYS * 86_400_000);
  switch (type) {
    case 'TEXT': return t('fieldEditor.preview.text');
    case 'NUMBER': return draft.settings.type === 'percentage' ? 37.5 : 1234.5678;
    case 'CURRENCY': return { amountMicros: 1_250_500_000, currencyCode: draft.defaultDraft.text || 'BRL' };
    case 'DATE': return past.toISOString().slice(0, 10);
    case 'DATE_TIME': return past.toISOString();
    case 'EMAILS': return { primaryEmail: 'secretaria@escola.example', additionalEmails: [] };
    case 'LINKS': return { primaryLinkUrl: 'https://escola.example', primaryLinkLabel: '', secondaryLinks: [] };
    case 'PHONES': return { primaryPhoneNumber: '11987654321', primaryPhoneCountryCode: 'BR', primaryPhoneCallingCode: '+55', additionalPhones: [] };
    case 'RATING': return 'RATING_4';
    case 'BOOLEAN': return true;
    case 'UUID': return '6f9619ff-8b86-4011-b42d-00c04fc964ff';
    case 'SELECT': return options[0]?.value ?? null;
    case 'MULTI_SELECT': return options.slice(0, 2).map((option) => option.value);
    default: return null;
  }
}

/**
 * Select simples com rótulo, no padrão dos formulários do modelo de dados.
 * @param {{ label: string, value: string, options: ChoiceOption[], onChange: (value: string) => void, disabled?: boolean, hint?: string }} props
 */
function ChoiceField({ label, value, options, onChange, disabled = false, hint }) {
  return html`<div class="form-field">
    <label class="form-field">
      <span class="form-field-label">${label}</span>
      <select class="text-input data-model-select" value=${value} disabled=${disabled} aria-label=${label}
        onChange=${(/** @type {Event} */ event) => onChange(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
        ${options.map((option) => html`<option key=${option.value} value=${option.value}>${option.label}</option>`)}
      </select>
    </label>
    ${hint ? html`<span class="input-hint">${hint}</span>` : null}
  </div>`;
}

/**
 * Campo numérico inteiro com limites.
 * @param {{ label: string, value: number, minimum: number, maximum: number, onChange: (value: number) => void, disabled?: boolean, hint?: string }} props
 */
function IntegerField({ label, value, minimum, maximum, onChange, disabled = false, hint }) {
  return html`<div class="form-field">
    <label class="form-field">
      <span class="form-field-label">${label}</span>
      <input class="text-input field-editor-number" type="number" inputmode="numeric" min=${minimum} max=${maximum} step="1" value=${value} disabled=${disabled}
        aria-label=${label}
        onInput=${(/** @type {Event} */ event) => {
          const parsed = Number.parseInt(/** @type {HTMLInputElement} */ (event.currentTarget).value, 10);
          if (Number.isFinite(parsed)) onChange(Math.min(maximum, Math.max(minimum, parsed)));
        }} />
    </label>
    ${hint ? html`<span class="input-hint">${hint}</span>` : null}
  </div>`;
}

/**
 * Controles de settings do tipo (doc 04, 2.5).
 * @param {{ type: string, draft: CustomizationDraft, isCreate: boolean, disabled: boolean, onSettingsChange: (patch: Record<string, any>) => void }} props
 */
function TypeSettingsControls({ type, draft, isCreate, disabled, onSettingsChange }) {
  const settings = draft.settings;
  const set = (/** @type {string} */ key, /** @type {unknown} */ value) => onSettingsChange({ [key]: value });
  switch (type) {
    case 'NUMBER':
      return html`
        <${ChoiceField} label=${t('fieldEditor.settings.numberType')} value=${settings.type ?? 'number'} disabled=${disabled}
          options=${NUMBER_VARIANTS.map((value) => ({ value, label: t(`fieldEditor.settings.numberTypes.${value}`) }))}
          onChange=${(value) => set('type', value === 'number' ? null : value)} />
        <${IntegerField} label=${t('fieldEditor.settings.decimals')} value=${settings.decimals ?? 0} minimum=${0} maximum=${MAX_NUMBER_DECIMALS} disabled=${disabled}
          onChange=${(value) => set('decimals', value)} />
        ${isCreate
          ? html`<${ChoiceField} label=${t('fieldEditor.settings.dataType')} value=${settings.dataType ?? 'float'} disabled=${disabled}
              hint=${t('fieldEditor.settings.dataTypeHint')}
              options=${NUMBER_DATA_TYPES.map((value) => ({ value, label: t(`fieldEditor.settings.dataTypes.${value}`) }))}
              onChange=${(value) => set('dataType', value === 'float' ? null : value)} />`
          : null}`;
    case 'CURRENCY':
      return html`
        <${ChoiceField} label=${t('fieldEditor.settings.currencyFormat')} value=${settings.format ?? 'full'} disabled=${disabled}
          options=${CURRENCY_FORMATS.map((value) => ({ value, label: t(`fieldEditor.settings.currencyFormats.${value}`) }))}
          onChange=${(value) => set('format', value === 'full' ? null : value)} />
        ${settings.format !== 'short'
          ? html`<${IntegerField} label=${t('fieldEditor.settings.decimals')} value=${settings.decimals ?? 2} minimum=${0} maximum=${MAX_CURRENCY_DECIMALS}
              disabled=${disabled} onChange=${(value) => set('decimals', value)} />`
          : null}`;
    case 'DATE':
    case 'DATE_TIME':
      return html`<${ChoiceField} label=${t('fieldEditor.settings.dateDisplay')} value=${settings.displayFormat ?? 'USER_SETTINGS'} disabled=${disabled}
        hint=${t('fieldEditor.settings.dateDisplayHint')}
        options=${DATE_DISPLAY_FORMATS.map((value) => ({ value, label: t(`fieldEditor.settings.dateDisplays.${value}`) }))}
        onChange=${(value) => set('displayFormat', value === 'USER_SETTINGS' ? null : value)} />`;
    case 'TEXT':
      return html`<${IntegerField} label=${t('fieldEditor.settings.displayedMaxRows')} value=${settings.displayedMaxRows ?? 0} minimum=${0} maximum=${MAX_DISPLAYED_ROWS}
        disabled=${disabled} hint=${t('fieldEditor.settings.displayedMaxRowsHint')} onChange=${(value) => set('displayedMaxRows', value === 0 ? null : value)} />`;
    case 'EMAILS':
    case 'LINKS':
    case 'PHONES':
      return html`<${ChoiceField} label=${t('fieldEditor.settings.clickAction')} value=${settings.clickAction ?? 'OPEN_LINK'} disabled=${disabled}
        hint=${t(`fieldEditor.settings.clickActionHint.${type}`)}
        options=${CLICK_ACTIONS.map((value) => ({ value, label: t(`fieldEditor.settings.clickActions.${type}.${value}`) }))}
        onChange=${(value) => set('clickAction', value === 'OPEN_LINK' ? null : value)} />`;
    default:
      return null;
  }
}

/**
 * Editor do valor padrão para os tipos de DEFAULT_VALUE_EDITOR_TYPES.
 * @param {{ type: string, draft: CustomizationDraft, options: Array<{ value: string, label: string }>, disabled: boolean,
 *   onDefaultChange: (patch: Partial<CustomizationDraft['defaultDraft']>) => void }} props
 */
function DefaultValueControl({ type, draft, options, disabled, onDefaultChange }) {
  const defaultDraft = draft.defaultDraft;
  const label = t('fieldEditor.defaultValue.label');
  /** @param {string} text */
  const setText = (text) => onDefaultChange({ mode: text === '' ? 'none' : 'value', text });
  const none = { value: '', label: t('fieldEditor.defaultValue.none') };
  switch (type) {
    case 'TEXT':
    case 'NUMBER':
      return html`<label class="form-field">
        <span class="form-field-label">${label}</span>
        <input class="text-input" value=${defaultDraft.text} disabled=${disabled} aria-label=${label} inputmode=${type === 'NUMBER' ? 'decimal' : undefined}
          placeholder=${t('fieldEditor.defaultValue.placeholder')} maxlength="255"
          onInput=${(/** @type {Event} */ event) => setText(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
      </label>`;
    case 'UUID':
      return html`<${ChoiceField} label=${label} value=${defaultDraft.mode === 'uuid' ? 'uuid' : ''} disabled=${disabled}
        options=${[none, { value: 'uuid', label: t('fieldEditor.defaultValue.generateUuid') }]}
        onChange=${(value) => onDefaultChange({ mode: value === 'uuid' ? 'uuid' : 'none', text: '' })} />`;
    case 'BOOLEAN':
      return html`<${ChoiceField} label=${label} value=${defaultDraft.mode === 'value' ? defaultDraft.text : ''} disabled=${disabled}
        options=${[none, { value: 'true', label: t('common.true') }, { value: 'false', label: t('common.false') }]} onChange=${setText} />`;
    case 'RATING':
      return html`<${ChoiceField} label=${label} value=${defaultDraft.mode === 'value' ? defaultDraft.text : ''} disabled=${disabled}
        options=${[none, ...RATING_STEPS.map((step) => ({ value: `RATING_${step}`, label: t('fieldEditor.defaultValue.stars', { count: step }) }))]} onChange=${setText} />`;
    case 'CURRENCY':
      return html`<${ChoiceField} label=${t('fieldEditor.defaultValue.currency')} value=${defaultDraft.mode === 'value' ? defaultDraft.text : ''} disabled=${disabled}
        hint=${t('fieldEditor.defaultValue.currencyHint')}
        options=${[{ value: '', label: t('fieldEditor.defaultValue.noCurrency') }, ...CURRENCY_CODES.map((code) => ({ value: code, label: `${currencyName(code)} (${code})` }))]}
        onChange=${setText} />`;
    case 'PHONES':
      return html`<${ChoiceField} label=${t('fieldEditor.defaultValue.country')} value=${defaultDraft.mode === 'value' ? defaultDraft.text : ''} disabled=${disabled}
        hint=${t('fieldEditor.defaultValue.countryHint')}
        options=${[{ value: '', label: t('fieldEditor.defaultValue.noCountry') }, ...PHONE_COUNTRIES.map((country) => ({ value: country.countryCode, label: `${country.flag} ${country.label} (${country.callingCode})` }))]}
        onChange=${setText} />`;
    case 'DATE':
    case 'DATE_TIME': {
      const mode = defaultDraft.mode === 'now' ? 'now' : defaultDraft.mode === 'value' ? 'value' : '';
      return html`<div class="field-editor-default-date">
        <${ChoiceField} label=${label} value=${mode} disabled=${disabled}
          options=${[none, { value: 'now', label: t(`fieldEditor.defaultValue.now.${type}`) }, { value: 'value', label: t('fieldEditor.defaultValue.fixedDate') }]}
          onChange=${(value) => onDefaultChange({ mode: value === 'now' ? 'now' : value === 'value' ? 'value' : 'none', text: value === 'value' ? defaultDraft.text : '' })} />
        ${mode === 'value'
          ? html`<label class="form-field">
              <span class="form-field-label">${t('fieldEditor.defaultValue.fixedDate')}</span>
              <input class="text-input" type=${type === 'DATE' ? 'date' : 'datetime-local'} value=${defaultDraft.text} disabled=${disabled}
                onInput=${(/** @type {Event} */ event) => onDefaultChange({ mode: 'value', text: /** @type {HTMLInputElement} */ (event.currentTarget).value })} />
            </label>`
          : null}
      </div>`;
    }
    case 'MULTI_SELECT':
      return html`<fieldset class="form-field field-editor-default-options" disabled=${disabled}>
        <legend class="form-field-label">${label}</legend>
        ${options.length === 0 ? html`<span class="input-hint">${t('fieldEditor.defaultValue.noOptions')}</span>` : null}
        ${options.map((option) => html`<label key=${option.value} class="data-model-toggle-label">
          <input type="checkbox" checked=${defaultDraft.values.includes(option.value)}
            onChange=${(/** @type {Event} */ event) => {
              const checked = /** @type {HTMLInputElement} */ (event.currentTarget).checked;
              const values = checked ? [...defaultDraft.values, option.value] : defaultDraft.values.filter((value) => value !== option.value);
              onDefaultChange({ mode: values.length > 0 ? 'value' : 'none', values });
            }} />
          <span>${translateMetadataLabel(option.label)}</span>
        </label>`)}
      </fieldset>`;
    default:
      return null;
  }
}

/**
 * @param {{ field: FieldMetadata | null, type: string, label: string, icon: string, draft: CustomizationDraft,
 *   options: Array<{ value: string, label: string, color?: string }>, disabled: boolean, isFunnelField: boolean,
 *   onChange: (draft: CustomizationDraft) => void, defaultError: string | null }} props
 */
export function FieldCustomizationSections({ field, type, label, icon, draft, options, disabled, isFunnelField, onChange, defaultError }) {
  const isCreate = field === null;
  const hasSettings = ['NUMBER', 'CURRENCY', 'DATE', 'DATE_TIME', 'TEXT', 'EMAILS', 'LINKS', 'PHONES'].includes(type);
  const hasDefault = DEFAULT_VALUE_EDITOR_TYPES.includes(type);
  const canBeUnique = canTypeBeUnique(type) && !isFunnelField;
  const canBeIndexed = canFieldBeIndexed(field, type);
  const uniqueLockReason = getUniqueLockReason(field);
  const previewRecord = { [field?.name ?? 'preview']: previewValue(type, draft, options) };
  /** @type {any} */
  const previewField = {
    ...(field ?? { id: 'preview', objectMetadataId: '', name: 'preview' }),
    type, label, icon, options: options.map((option, position) => ({ id: option.value, ...option, position })), settings: draft.settings,
  };

  const customization = hasSettings || hasDefault || type === 'RATING' || type === 'BOOLEAN'
    ? html`<${SettingsSection} title=${t('fieldEditor.customization.title')} description=${t('fieldEditor.customization.description')}>
        <div class="field-editor-preview" aria-label=${t('fieldEditor.preview.title')}>
          <span class="field-editor-preview-label"><${Icon} name=${icon} size=${16} />${label || t('fieldEditor.preview.untitled')}</span>
          <span class="field-editor-preview-value"><${FieldDisplay} field=${previewField} record=${previewRecord} emptyLabel=${t('fieldEditor.preview.empty')} /></span>
        </div>
        <div class="field-editor-grid">
          ${hasSettings
            ? html`<${TypeSettingsControls} type=${type} draft=${draft} isCreate=${isCreate} disabled=${disabled}
                onSettingsChange=${(patch) => {
                  const settings = { ...draft.settings, ...patch };
                  for (const [key, value] of Object.entries(settings)) if (value === null || value === undefined) delete settings[key];
                  onChange({ ...draft, settings });
                }} />`
            : null}
          ${hasDefault
            ? html`<${DefaultValueControl} type=${type} draft=${draft} options=${options} disabled=${disabled}
                onDefaultChange=${(patch) => onChange({ ...draft, defaultDraft: { ...draft.defaultDraft, ...patch } })} />`
            : null}
        </div>
        ${type === 'RATING' ? html`<p class="input-hint">${t('fieldEditor.settings.ratingHint')}</p>` : null}
        ${defaultError ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${t(defaultError)}</span></div>` : null}
      <//>`
    : null;

  const values = canBeUnique || canBeIndexed
    ? html`<${SettingsSection} title=${t('fieldEditor.values.title')}
        description=${draft.isUnique ? t('fieldEditor.values.descriptionUnique') : t('fieldEditor.values.description')}>
        <div class="field-editor-toggles">
          ${canBeUnique
            ? html`<div class="field-editor-toggle-row">
                <${Icon} name="IconKey" size=${16} />
                <span class="field-editor-toggle-text">
                  <strong>${t('fieldEditor.values.unique')}</strong>
                  <small>${uniqueLockReason ? t(uniqueLockReason) : t('fieldEditor.values.uniqueHint')}</small>
                </span>
                <${Toggle} value=${draft.isUnique} disabled=${disabled || Boolean(uniqueLockReason)} ariaLabel=${t('fieldEditor.values.unique')}
                  onChange=${(/** @type {boolean} */ isUnique) => onChange({ ...draft, isUnique })} />
              </div>`
            : null}
          ${canBeIndexed
            ? html`<div class="field-editor-toggle-row">
                <${Icon} name="IconRocket" size=${16} />
                <span class="field-editor-toggle-text">
                  <strong>${t('fieldEditor.values.index')}</strong>
                  <small>${draft.isUnique ? t('fieldEditor.values.indexCoveredByUnique') : t('fieldEditor.values.indexHint')}</small>
                </span>
                <${Toggle} value=${draft.isIndexed || draft.isUnique} disabled=${disabled || draft.isUnique} ariaLabel=${t('fieldEditor.values.index')}
                  onChange=${(/** @type {boolean} */ isIndexed) => onChange({ ...draft, isIndexed })} />
              </div>`
            : null}
        </div>
      <//>`
    : null;

  return html`${customization}${values}`;
}
